import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { useAppT } from "../components/home/i18n";
import { DatabaseBackup, Play, RotateCcw, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";

const fmtSize = (b) => (b == null ? "—" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`);

function RestoreReport({ report, a }) {
  const cols = Object.entries(report.collections || {});
  const integ = report.integrity || {};
  return (
    <div className="mt-3 rounded-sm border border-[#E8E4DB] bg-[#FDFBF7] p-3 text-xs" data-testid="restore-report">
      <div className="flex flex-wrap gap-4 font-semibold">
        <span className={report.fully_recoverable ? "text-[#2E6B4B]" : "text-[#8B3A3A]"} data-testid="fully-recoverable">
          {report.fully_recoverable ? <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> : <XCircle className="mr-1 inline h-3.5 w-3.5" />}
          {a.adminBackups.fullyRecoverable} {String(!!report.fully_recoverable)}
        </span>
        <span className="text-[#2C2A29]">{a.adminBackups.recordsMatch} {String(report.all_counts_match)}</span>
        <span className="text-[#2C2A29]">{a.adminBackups.mediaRestored} {report.media_restored}/{report.media_total}</span>
        <span className="text-[#2C2A29]">{a.adminBackups.sampleOpened} {String(report.sample_media_opened)}</span>
        <span className="text-[#5C5855]">→ {report.restore_db}</span>
      </div>
      {integ.missing > 0 && <div className="mt-1 text-[#8B3A3A]">⚠ {integ.missing} {a.adminBackups.missingFiles}</div>}
      <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-0.5 md:grid-cols-3">
        {cols.map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <span className="text-[#5C5855]">{k}</span>
            <span className={v.match ? "text-[#2C2A29]" : "text-[#8B3A3A]"}>{v.restored}/{v.expected}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Integrity({ integ, a }) {
  if (!integ) return null;
  const bad = (integ.missing || 0) + (integ.error || 0);
  const recs = [...(integ.missing_records || []), ...(integ.error_records || [])];
  return (
    <div className={`mt-2 rounded-sm border p-2 text-[11px] ${bad ? "border-[#8B3A3A]/30 bg-[#8B3A3A]/6" : "border-[#2E6B4B]/25 bg-[#2E6B4B]/6"}`} data-testid="integrity-summary">
      <span className={bad ? "font-semibold text-[#8B3A3A]" : "font-semibold text-[#2E6B4B]"}>
        {a.adminBackups.integrity} {integ.present}/{integ.total} {a.adminBackups.presentWord}{integ.missing ? ` · ${integ.missing} ${a.adminBackups.missingWord}` : ""}{integ.error ? ` · ${integ.error} ${a.adminBackups.unverifiedWord}` : ""}
      </span>
      {recs.length > 0 && (
        <div className="mt-1 max-h-40 overflow-auto" data-testid="integrity-affected">
          {recs.map((r) => (
            <div key={r.id} className="text-[#8B3A3A]">{r.status}: {r.coll} · {r.kind}{r.owner_alias ? ` · ${r.owner_alias}` : ""}{r.in_approved_snapshot ? ` · ${a.adminBackups.employerVisible}` : ""} · {r.path}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminBackups() {
  const { a } = useAppT();
  const [data, setData] = useState({ backups: [], retention_keep: 7, restore_db: "" });
  const [busy, setBusy] = useState(false);
  const [jobByBackup, setJobByBackup] = useState({});
  const [report, setReport] = useState({});
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = async (inc = includeArchived) => { const { data } = await API.get("/admin/backups", { params: { include_archived: inc } }); setData(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const runBackup = async () => {
    setBusy(true);
    try { await API.post("/admin/backups/run"); toast.success(a.adminBackups.restoreStarted); setTimeout(() => load(), 8000); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pollJob = (backupId, jobId) => {
    const t = setInterval(async () => {
      try {
        const { data: j } = await API.get(`/admin/backups/restore-jobs/${jobId}`);
        setJobByBackup((m) => ({ ...m, [backupId]: j }));
        if (j.status === "completed" || j.status === "failed") {
          clearInterval(t);
          if (j.report) setReport((r) => ({ ...r, [backupId]: j.report }));
          if (j.status === "completed") toast.success(j.report?.fully_recoverable ? a.adminBackups.restoreVerified : a.adminBackups.restoreRan);
          else toast.error(a.adminBackups.restoreFailed + (j.error || ""));
        }
      } catch { clearInterval(t); }
    }, 3000);
  };

  const restore = async (id) => {
    try {
      const { data: res } = await API.post(`/admin/backups/${id}/restore-test`);
      setJobByBackup((m) => ({ ...m, [id]: res.job }));
      toast.success(a.adminBackups.restoreTestStarted);
      pollJob(id, res.job.id);
    } catch (e) {
      if (e.response?.status === 409) toast.error(a.adminBackups.alreadyRunning);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const archive = async (id) => {
    try { await API.post(`/admin/backups/${id}/archive`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stLabel = (s) => a.adminBackups.statuses[s === "active_incomplete" ? "incomplete" : s] || s;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminBackups.title}</h1>
        <button data-testid="run-backup-btn" disabled={busy} onClick={runBackup} className="inline-flex items-center gap-1 rounded-full bg-[#1F4A38] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Play className="h-4 w-4" /> {a.adminBackups.runNow}</button>
      </div>
      <div className="mt-2 flex items-start gap-2 rounded-sm bg-[#C4A46B]/10 p-3 text-xs text-[#8a6f38]">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{a.adminBackups.note1}{data.retention_keep}{a.adminBackups.note2}<b>{a.adminBackups.noteNot}</b>{a.adminBackups.note3}<b>{data.restore_db}</b>{a.adminBackups.note4}<code>/app/docs/BACKUP_RESTORE.md</code>.</span>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-[#5C5855]">
          <input data-testid="toggle-archived" type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="h-4 w-4 accent-[#1F4A38]" />
          {a.adminBackups.showArchived}
        </label>
      </div>

      <div className="mt-2 space-y-3" data-testid="backups-list">
        {data.backups.length === 0 && <div className="text-sm text-[#5C5855]">{a.adminBackups.noBackups}</div>}
        {data.backups.map((b) => {
          const job = jobByBackup[b.id];
          const running = job && (job.status === "queued" || job.status === "running");
          return (
          <div key={b.id} data-testid={`backup-${b.id}`} className={`rounded-md border bg-white p-4 ${b.archived ? "border-dashed border-[#E8E4DB] opacity-70" : "border-[#E8E4DB]"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <DatabaseBackup className="h-5 w-5 text-[#C4A46B]" />
                <div>
                  <div className="text-sm font-medium text-[#2C2A29]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"} <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.status === "active" ? "bg-[#2E6B4B]/12 text-[#2E6B4B]" : b.status === "failed" ? "bg-[#8B3A3A]/10 text-[#8B3A3A]" : b.status === "active_incomplete" ? "bg-[#D28E3D]/15 text-[#8a5a1f]" : "bg-[#5C5855]/10 text-[#5C5855]"}`}>{stLabel(b.status)}</span>{b.archived && <span className="ml-1 text-[10px] text-[#8a8580]">{a.adminBackups.statuses.archived}</span>}</div>
                  <div className="text-[11px] text-[#5C5855]">{fmtSize(b.size)} · {b.media_count ?? 0} {a.adminBackups.mediaWord} · {Object.keys(b.collection_counts || {}).length} {a.adminBackups.collectionsWord} · {b.trigger}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(b.status === "active" || b.status === "active_incomplete") && (
                  <button data-testid={`restore-${b.id}`} disabled={running} onClick={() => restore(b.id)} className="inline-flex items-center gap-1 rounded-full border border-[#1F4A38] px-4 py-1.5 text-xs font-semibold text-[#1F4A38] disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> {running ? (job.progress || a.adminBackups.running) : a.adminBackups.runRestoreTest}</button>
                )}
                {(b.status === "failed" || b.status === "active_incomplete" || b.archived) && (
                  <button data-testid={`archive-${b.id}`} onClick={() => archive(b.id)} className="rounded-full border border-[#E8E4DB] px-3 py-1.5 text-xs text-[#5C5855]">{b.archived ? a.adminBackups.unarchive : a.adminBackups.dismiss}</button>
                )}
              </div>
            </div>
            {running && <div className="mt-1 text-[11px] text-[#8a5a1f]" data-testid={`restore-status-${b.id}`}>{a.adminBackups.runRestoreTest} {job.status}: {job.progress}</div>}
            {b.error && <div className="mt-1 text-xs text-[#8B3A3A]">{a.adminBackups.errorPrefix} {b.error}</div>}
            <Integrity integ={b.integrity} a={a} />
            {report[b.id] && <RestoreReport report={report[b.id]} a={a} />}
          </div>
          );
        })}
      </div>
    </AdminLayout>
  );
}
